(() => {
  'use strict';

  const initialized = new WeakSet();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  };

  const lockVideoChrome = (video) => {
    if (!video) return;
    video.controls = false;
    video.disablePictureInPicture = true;
    video.setAttribute('controlslist', 'nodownload noremoteplayback');
    video.setAttribute('disablepictureinpicture', '');
    video.addEventListener('contextmenu', (event) => event.preventDefault());
    video.addEventListener('dragstart', (event) => event.preventDefault());
  };

  const initialize = (section) => {
    if (!section || initialized.has(section)) return;
    initialized.add(section);

    const rail = section.querySelector('[data-ugc-rail]');
    const stories = [...section.querySelectorAll('[data-ugc-story]')];
    const previews = [...section.querySelectorAll('[data-ugc-preview]')];
    const previous = section.querySelector('[data-ugc-previous]');
    const next = section.querySelector('[data-ugc-next]');
    const position = section.querySelector('[data-ugc-position]');
    const status = section.querySelector('[data-ugc-status]');
    const dialog = section.querySelector('[data-ugc-dialog]');
    const dialogMedia = section.querySelector('[data-ugc-dialog-media]');
    const dialogTitle = section.querySelector('[data-ugc-dialog-title]');
    const dialogIntro = section.querySelector('[data-ugc-dialog-intro]');
    const dialogProducts = section.querySelector('[data-ugc-dialog-products]');
    const idleVideo = section.querySelector('[data-ugc-video]');
    let video = idleVideo;
    let previewSlot = null;
    let previewClasses = '';
    const playbackStatus = section.querySelector('[data-ugc-playback-status]');
    const playButton = section.querySelector('[data-ugc-play]');
    const bigPlayButton = section.querySelector('[data-ugc-big-play]');
    const progress = section.querySelector('[data-ugc-progress]');
    const time = section.querySelector('[data-ugc-time]');
    const muteButton = section.querySelector('[data-ugc-mute]');
    const fullscreenButton = section.querySelector('[data-ugc-fullscreen]');
    let currentIndex = 0;
    let returnFocus = null;
    let scrollTimer = 0;
    let previewFrame = 0;
    let previewObserver = null;
    let sectionNearViewport = false;
    let playbackSession = 0;
    let playbackWanted = false;
    let startupTimer = 0;
    let fallbackSource = '';
    let usedFallback = false;
    let rebufferCount = 0;
    const saveData = () => navigator.connection?.saveData === true;

    if (!rail || !dialog || !dialogMedia || !video) return;

    lockVideoChrome(video);
    previews.forEach((preview) => {
      lockVideoChrome(preview);
      preview.defaultMuted = true;
      preview.muted = true;
      preview.loop = true;
      preview.playsInline = true;
      preview.pause();
      preview.addEventListener('canplay', () => preview.classList.add('is-ready'));
      preview.addEventListener('error', () => preview.classList.remove('is-ready'));
    });

    const preferredSource = (trigger) => {
      // Native HLS was tested, but restarting its multi-second segments when a
      // preview returns to the beginning delayed first playback on Safari.
      // The audited original is fast-start H.264, not the raw camera source.
      if (saveData() && trigger.dataset.compactUrl) return trigger.dataset.compactUrl;
      return trigger.dataset.optimizedUrl || trigger.dataset.videoUrl;
    };

    const ensurePreviewSource = (preview) => {
      if (preview.getAttribute('src') || !preview.dataset.previewSrc) return;
      // Metadata warming also works when low-power mode blocks muted autoplay.
      // Only the visible cards are warmed, never the whole 15-video rail.
      preview.preload = 'metadata';
      preview.src = preferredSource(preview.closest('[data-ugc-open]'));
      preview.load();
    };

    const releasePreview = (preview) => {
      if (dialog.open && preview === video) return;
      preview.pause();
      if (!preview.hasAttribute('src')) return;
      preview.removeAttribute('src');
      preview.preload = 'none';
      preview.classList.remove('is-ready');
      preview.load();
    };

    const playPreview = (preview) => {
      if (!sectionNearViewport || reduceMotion() || saveData() || document.hidden || dialog.open) return;
      ensurePreviewSource(preview);
      preview.play().catch(() => {});
    };

    const pausePreviews = () => previews.forEach((preview) => {
      if (dialog.open && preview === video) return;
      preview.pause();
    });

    const resumeVisiblePreviews = () => {
      if (dialog.open) return;
      if (!sectionNearViewport || document.hidden) {
        previews.forEach(releasePreview);
        return;
      }
      const railRect = rail.getBoundingClientRect();
      const visible = previews.map((preview) => {
        const rect = preview.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, railRect.right) - Math.max(rect.left, railRect.left));
        return { preview, visibleWidth, width: rect.width };
      }).filter((item) => item.visibleWidth >= item.width * .35)
        .sort((left, right) => right.visibleWidth - left.visibleWidth);
      const activeLimit = window.matchMedia('(min-width: 768px)').matches ? 2 : 1;
      const active = new Set(visible.slice(0, activeLimit).map((item) => item.preview));
      previews.forEach((preview) => {
        if (!active.has(preview)) { releasePreview(preview); return; }
        if (!saveData()) ensurePreviewSource(preview);
        playPreview(preview);
      });
    };

    const schedulePreviewRefresh = () => {
      window.cancelAnimationFrame(previewFrame);
      previewFrame = window.requestAnimationFrame(resumeVisiblePreviews);
    };

    const activatePreviewObserver = () => {
      if (previewObserver || !sectionNearViewport) return;
      if ('IntersectionObserver' in window) {
        previewObserver = new IntersectionObserver(schedulePreviewRefresh, {
          root: rail,
          rootMargin: '0px 80px',
          threshold: [.05, .35, .8],
        });
        previews.forEach((preview) => previewObserver.observe(preview));
      }
      schedulePreviewRefresh();
    };

    if ('IntersectionObserver' in window) {
      const sectionObserver = new IntersectionObserver(([entry]) => {
        sectionNearViewport = entry.isIntersecting;
        if (sectionNearViewport) activatePreviewObserver();
        schedulePreviewRefresh();
      }, { rootMargin: '400px 0px', threshold: 0 });
      sectionObserver.observe(section);
    } else {
      sectionNearViewport = true;
      activatePreviewObserver();
    }

    const setCurrentIndex = (index) => {
      currentIndex = Math.max(0, Math.min(stories.length - 1, index));
      if (previous) previous.disabled = currentIndex <= 0;
      if (next) next.disabled = currentIndex >= stories.length - 1;
      if (position) position.textContent = stories.length ? `${currentIndex + 1} / ${stories.length}` : '—';
    };

    const nearestStoryIndex = () => stories.reduce((nearest, story, index) => {
      const distance = Math.abs(story.offsetLeft - rail.offsetLeft - rail.scrollLeft);
      return distance < nearest.distance ? { index, distance } : nearest;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;

    const updateFromScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        setCurrentIndex(nearestStoryIndex());
        schedulePreviewRefresh();
      }, 70);
    };

    const move = (direction) => {
      const targetIndex = Math.max(0, Math.min(stories.length - 1, currentIndex + direction));
      if (targetIndex === currentIndex) return;
      setCurrentIndex(targetIndex);
      stories[targetIndex]?.scrollIntoView({
        behavior: reduceMotion() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    };

    const setPlaybackState = () => {
      const playing = !video.paused && !video.ended;
      dialogMedia.classList.toggle('is-playing', playing);
      if (bigPlayButton) bigPlayButton.hidden = playing;
      if (playButton) playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    };

    const setBuffering = (buffering, message = '') => {
      dialogMedia.setAttribute('aria-busy', String(buffering));
      if (playbackStatus) {
        playbackStatus.hidden = !buffering && !message;
        playbackStatus.textContent = message || 'Loading video…';
      }
    };

    const clearStartupTimer = () => window.clearTimeout(startupTimer);

    const recoverPlayback = () => {
      if (!dialog.open || !playbackWanted || usedFallback || !fallbackSource || video.getAttribute('src') === fallbackSource) return false;
      usedFallback = true;
      playbackSession += 1;
      const position = video.currentTime;
      video.src = fallbackSource;
      video.preload = 'auto';
      video.load();
      try { video.currentTime = position; } catch (_error) { /* A cold file may not have metadata yet. */ }
      playVideo();
      return true;
    };

    const armStartupRecovery = () => {
      clearStartupTimer();
      if (!playbackWanted || usedFallback || !fallbackSource || video.getAttribute('src') === fallbackSource) return;
      const session = playbackSession;
      startupTimer = window.setTimeout(() => {
        if (session !== playbackSession || !dialog.open || !playbackWanted || video.readyState >= 3) return;
        recoverPlayback();
      }, video.currentTime > .1 ? 2000 : 4500);
    };

    const playVideo = () => {
      const requestedVideo = video;
      const session = playbackSession;
      playbackWanted = true;
      if (video.error) video.load();
      video.preload = 'auto';
      setBuffering(video.readyState < 3);
      armStartupRecovery();
      // Keep this call in the tap/click handler: Safari requires a user gesture
      // when an inline, muted preview becomes a video with sound.
      video.play().then(() => {
        if (video === requestedVideo && session === playbackSession && dialog.open) { clearStartupTimer(); setBuffering(false); }
      }).catch((error) => {
        if (video !== requestedVideo || session !== playbackSession || !dialog.open || error.name === 'AbortError') return;
        if (error.name !== 'NotAllowedError' && recoverPlayback()) return;
        clearStartupTimer();
        playbackWanted = false;
        setBuffering(false, error.name === 'NotAllowedError' ? 'Tap play to start the video.' : 'Video could not load. Tap play to retry.');
        setPlaybackState();
      });
    };

    const setMuteState = () => {
      dialogMedia.classList.toggle('is-muted', video.muted);
      muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    };

    const updateTime = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (progress && duration > 0) progress.value = String(Math.round((video.currentTime / duration) * 1000));
      if (time) time.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
    };

    const togglePlayback = () => {
      if (video.paused || video.ended) playVideo();
      else { playbackWanted = false; clearStartupTimer(); video.pause(); }
    };

    const resetVideo = () => {
      playbackSession += 1;
      playbackWanted = false;
      clearStartupTimer();
      video.pause();
      try { video.currentTime = 0; } catch (_error) { /* Metadata may not be available yet. */ }
      if (previewSlot) {
        // Return the same decoder/buffer to its card. Do not download a second
        // copy just because the customer opens or closes the dialog.
        const preview = video;
        preview.replaceWith(idleVideo);
        preview.className = previewClasses;
        if (preview.readyState >= 3) preview.classList.add('is-ready');
        preview.removeAttribute('data-ugc-video');
        preview.setAttribute('aria-hidden', 'true');
        preview.tabIndex = -1;
        preview.muted = true;
        preview.loop = true;
        previewSlot.replaceWith(preview);
        previewSlot = null;
        video = idleVideo;
      } else {
        video.removeAttribute('src');
        video.removeAttribute('poster');
        video.load();
      }
      setBuffering(false);
      if (progress) progress.value = '0';
      if (time) time.textContent = '0:00 / 0:00';
      dialogMedia.classList.remove('is-playing', 'is-muted');
      if (bigPlayButton) bigPlayButton.hidden = false;
    };

    const closeDialog = () => {
      resetVideo();
      if (dialog.open) dialog.close();
    };

    const openDialog = (trigger) => {
      const preview = trigger.querySelector('[data-ugc-preview]');
      const source = preview?.getAttribute('src') || preferredSource(trigger);
      if (!source || typeof dialog.showModal !== 'function') return;
      playbackSession += 1;
      usedFallback = false;
      rebufferCount = 0;
      fallbackSource = trigger.dataset.compactUrl || trigger.dataset.videoUrl;
      returnFocus = trigger;
      const storyLabel = trigger.dataset.storyLabel || 'A Siwa scent story';
      const products = trigger.closest('[data-ugc-story]')?.querySelector('[data-ugc-products]');
      const productLinks = products ? [...products.children].map((link) => link.cloneNode(true)) : [];

      if (dialogTitle) dialogTitle.textContent = productLinks.length ? 'Fragrances in this video' : storyLabel;
      if (dialogIntro) dialogIntro.textContent = productLinks.length
        ? `The scents featured in “${storyLabel}”.`
        : 'A customer video about Siwa fragrance.';
      if (dialogProducts) dialogProducts.replaceChildren(...productLinks);

      dialog.showModal();
      document.body.classList.add('siwa-ugc-dialog-open');
      pausePreviews();
      if (preview) {
        previewSlot = document.createComment('Siwa video preview');
        previewClasses = preview.className;
        preview.replaceWith(previewSlot);
        idleVideo.replaceWith(preview);
        video = preview;
        video.className = 'siwa-ugc__dialog-video';
        video.setAttribute('data-ugc-video', '');
        video.removeAttribute('aria-hidden');
        video.removeAttribute('tabindex');
      }
      // Pausing alone doesn't cancel in-flight downloads from other cards.
      previews.forEach(candidate => { if (candidate !== video) releasePreview(candidate); });
      video.poster = trigger.dataset.posterSrc || '';
      if (video.getAttribute('src') !== source) {
        video.src = source;
        video.load();
      }
      video.loop = false;
      video.muted = false;
      try { video.currentTime = 0; } catch (_error) { /* First load may not have metadata. */ }
      setMuteState();
      setPlaybackState();
      playVideo();
      if (status) status.textContent = `${storyLabel} opened.`;
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    rail.addEventListener('scroll', updateFromScroll, { passive: true });
    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    });

    section.querySelectorAll('[data-ugc-open]').forEach((trigger, index) => {
      const warmIntent = () => {
        if (dialog.open) return;
        const preview = trigger.querySelector('[data-ugc-preview]');
        if (preview) ensurePreviewSource(preview);
      };
      trigger.addEventListener('pointerdown', warmIntent, { passive: true });
      trigger.addEventListener('focus', warmIntent);
      trigger.addEventListener('click', () => {
        if (dialog.open) return;
        setCurrentIndex(index);
        if (reduceMotion()) {
          openDialog(trigger);
          return;
        }
        trigger.classList.add('is-opening');
        trigger.setAttribute('aria-busy', 'true');
        // Open and play immediately; the wetting animation is visual only.
        openDialog(trigger);
        window.setTimeout(() => {
          trigger.classList.remove('is-opening');
          trigger.removeAttribute('aria-busy');
        }, 420);
      });
    });

    section.querySelector('[data-ugc-close]')?.addEventListener('click', closeDialog);
    playButton?.addEventListener('click', togglePlayback);
    bigPlayButton?.addEventListener('click', togglePlayback);
    [idleVideo, ...previews].forEach((candidate) => {
      const whenActive = (callback) => () => { if (candidate === video && dialog.open) callback(); };
      candidate.addEventListener('click', whenActive(togglePlayback));
      candidate.addEventListener('play', whenActive(setPlaybackState));
      candidate.addEventListener('pause', whenActive(() => { setBuffering(false); setPlaybackState(); }));
      candidate.addEventListener('ended', whenActive(setPlaybackState));
      candidate.addEventListener('waiting', whenActive(() => {
        setBuffering(!video.paused);
        if (playbackWanted && video.currentTime > .1 && !video.seeking) rebufferCount += 1;
        // Repeated short stalls also mean the connection cannot sustain this
        // rendition, even when none lasts long enough for the watchdog alone.
        if (rebufferCount >= 2 && recoverPlayback()) return;
        armStartupRecovery();
      }));
      candidate.addEventListener('playing', whenActive(() => { clearStartupTimer(); setBuffering(false); setPlaybackState(); }));
      candidate.addEventListener('error', whenActive(() => {
        if (!recoverPlayback()) { clearStartupTimer(); setBuffering(false, 'Video could not load. Tap play to retry.'); }
      }));
      for (const event of ['loadedmetadata', 'durationchange', 'timeupdate']) candidate.addEventListener(event, whenActive(updateTime));
    });

    progress?.addEventListener('input', () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = (Number(progress.value) / 1000) * video.duration;
      }
    });

    muteButton?.addEventListener('click', () => {
      video.muted = !video.muted;
      setMuteState();
    });

    fullscreenButton?.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen?.();
      else if (dialogMedia.requestFullscreen) dialogMedia.requestFullscreen();
      else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
    });

    document.addEventListener('fullscreenchange', () => {
      fullscreenButton?.setAttribute('aria-label', document.fullscreenElement ? 'Exit full screen' : 'View full screen');
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('cancel', () => resetVideo());
    dialog.addEventListener('close', () => {
      resetVideo();
      document.body.classList.remove('siwa-ugc-dialog-open');
      if (status) status.textContent = 'Video story closed.';
      resumeVisiblePreviews();
      returnFocus?.focus({ preventScroll: true });
      returnFocus = null;
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        playbackWanted = false;
        clearStartupTimer();
        video.pause();
        pausePreviews();
      } else if (!dialog.open) {
        resumeVisiblePreviews();
      }
    });

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      setCurrentIndex(nearestStoryIndex());
      schedulePreviewRefresh();
    }) : null;
    resizeObserver?.observe(rail);
    setCurrentIndex(0);
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll?.('[data-siwa-ugc]').forEach(initialize);
    if (root.matches?.('[data-siwa-ugc]')) initialize(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
